// src/pages/PipelineSentry.jsx
// Pipeline Sentry - AI-Powered Gas Pipeline Operations
// Edge AI Platform for Midstream Oil & Gas
// MWC Barcelona Demo - Celerway Stratus

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';

// ════════════════════════════════════════════════════════════════════════════
// MOCK PIPELINE DATA - Simulates gas transmission operations
// ════════════════════════════════════════════════════════════════════════════

const MOCK_STATION_GATEWAY = {
  id: 'gateway-cs-magnolia',
  name: 'Magnolia Compressor Station',
  location: 'Mile Post 47.2, East Texas',
  status: 'Online',
  assetCount: 14,
  activeAlarms: 4,
  stratusModel: 'Stratus 5G Industrial Hardened',
  connectivity: '5G + VSAT Backup',
  lastSync: new Date().toISOString(),
  pipelineSegment: 'Gulf Coast Mainline - Section 7',
  operatingPressure: 872,  // psig
  maxAllowablePressure: 1000,  // MAOP
  throughput: 847,  // MMcf/d (million cubic feet per day)
};

const MOCK_ASSETS = [
  // Compressor Units
  {
    id: 'COMP-01',
    type: 'Gas Compressor',
    category: 'compressor',
    model: 'Solar Titan 130',
    location: 'Compressor Bay 1',
    status: 'running',
    metrics: {
      suctionPressure: 412,    // psig
      dischargePressure: 872,  // psig
      flowRate: 285,           // MMcf/d
      speed: 9450,             // RPM
      vibration: 0.42,         // in/sec - ELEVATED
      baselineVibration: 0.28,
      bearingTemp: 185,        // °F
      oilPressure: 48,         // psig
      efficiency: 94.2,        // %
      hoursRun: 14250,
      nextOverhaul: 18000,
    },
    lastMaintenance: '2024-10-15',
  },
  {
    id: 'COMP-02',
    type: 'Gas Compressor',
    category: 'compressor',
    model: 'Solar Titan 130',
    location: 'Compressor Bay 2',
    status: 'running',
    metrics: {
      suctionPressure: 415,
      dischargePressure: 875,
      flowRate: 292,
      speed: 9520,
      vibration: 0.31,
      baselineVibration: 0.28,
      bearingTemp: 178,
      oilPressure: 52,
      efficiency: 95.8,
      hoursRun: 12800,
      nextOverhaul: 18000,
    },
    lastMaintenance: '2024-11-20',
  },
  {
    id: 'COMP-03',
    type: 'Gas Compressor',
    category: 'compressor',
    model: 'Solar Titan 130',
    location: 'Compressor Bay 3',
    status: 'standby',
    metrics: {
      suctionPressure: 410,
      dischargePressure: 0,
      flowRate: 0,
      speed: 0,
      vibration: 0,
      baselineVibration: 0.28,
      bearingTemp: 72,
      oilPressure: 0,
      efficiency: null,
      hoursRun: 8450,
      nextOverhaul: 18000,
    },
    lastMaintenance: '2024-12-05',
  },
  // Metering Stations
  {
    id: 'METER-INLET',
    type: 'Custody Meter',
    category: 'metering',
    model: 'Daniel Senior Orifice',
    location: 'Station Inlet',
    status: 'operational',
    metrics: {
      flowRate: 578.4,         // MMcf/d
      pressure: 415,
      temperature: 68,         // °F
      specificGravity: 0.62,
      btus: 1028,              // BTU/scf
      differential: 48.2,      // inches H2O
      uncertainty: 0.35,       // %
    },
    calibrationDue: '2025-03-15',
  },
  {
    id: 'METER-OUTLET',
    type: 'Custody Meter',
    category: 'metering',
    model: 'Daniel Senior Orifice',
    location: 'Station Outlet',
    status: 'warning',
    metrics: {
      flowRate: 565.2,         // MMcf/d - LOWER than inlet (imbalance!)
      pressure: 868,
      temperature: 94,
      specificGravity: 0.62,
      btus: 1028,
      differential: 42.8,
      uncertainty: 0.38,
    },
    calibrationDue: '2025-02-01',
  },
  // Motor Operated Valves (MOVs)
  {
    id: 'MOV-47-A',
    type: 'Isolation Valve',
    category: 'valve',
    model: 'Cameron Ball Valve 24"',
    location: 'Mainline Block - MP 47.0',
    status: 'open',
    metrics: {
      position: 100,           // % open
      strokeTime: 42,          // seconds
      baselineStroke: 38,
      motorAmps: 0,            // Currently idle
      actuatorPressure: 95,    // psig
      lastOperated: '2025-01-28T14:30:00Z',
      cycleCount: 847,
    },
  },
  {
    id: 'MOV-47-B',
    type: 'Isolation Valve',
    category: 'valve',
    model: 'Cameron Ball Valve 24"',
    location: 'Mainline Block - MP 47.5',
    status: 'fault',
    metrics: {
      position: 100,           // % open
      strokeTime: null,        // FAILED TO RESPOND
      baselineStroke: 40,
      motorAmps: 8.5,          // High - motor stalled?
      actuatorPressure: 42,    // LOW
      lastOperated: '2025-01-31T09:15:00Z',
      cycleCount: 923,
      faultCode: 'E-47: Actuator pressure low, stroke timeout',
    },
  },
  {
    id: 'MOV-48',
    type: 'Station Bypass',
    category: 'valve',
    model: 'Cameron Ball Valve 20"',
    location: 'Station Bypass',
    status: 'closed',
    metrics: {
      position: 0,
      strokeTime: 35,
      baselineStroke: 35,
      motorAmps: 0,
      actuatorPressure: 98,
      lastOperated: '2024-12-15T08:00:00Z',
      cycleCount: 124,
    },
  },
  // Leak Detection
  {
    id: 'LDS-SEC7',
    type: 'Leak Detection System',
    category: 'safety',
    model: 'Atmos Wave RT',
    location: 'Section 7 (MP 42-52)',
    status: 'alarm',
    metrics: {
      status: 'LEAK_SUSPECTED',
      confidence: 87,          // %
      estimatedLocation: 'MP 47.3 ± 0.2',
      estimatedRate: 2.4,      // MMcf/d
      pressureWaveDetected: true,
      acousticAnomaly: true,
      massBalanceDeviation: 2.28,  // %
      alarmTime: '2025-01-31T14:42:00Z',
    },
  },
  // Gas Detectors
  {
    id: 'GAS-DET-01',
    type: 'Gas Detector',
    category: 'safety',
    model: 'Honeywell Searchpoint',
    location: 'Compressor Building',
    status: 'operational',
    metrics: {
      lel: 0,                  // % Lower Explosive Limit
      h2s: 0,                  // ppm
      co: 2,                   // ppm
      alarmLEL: 10,            // Alarm threshold
      alarmH2S: 10,
      lastCalibration: '2025-01-15',
    },
  },
  {
    id: 'GAS-DET-02',
    type: 'Gas Detector',
    category: 'safety',
    model: 'Honeywell Searchpoint',
    location: 'Meter Building',
    status: 'warning',
    metrics: {
      lel: 8,                  // % LEL - ELEVATED
      h2s: 0,
      co: 5,
      alarmLEL: 10,
      alarmH2S: 10,
      lastCalibration: '2025-01-15',
    },
  },
  // Pig Tracking
  {
    id: 'PIG-TRACK',
    type: 'Pig Signaler',
    category: 'inspection',
    model: 'TD Williamson SmartBall',
    location: 'MP 47.2 Launcher/Receiver',
    status: 'active',
    metrics: {
      pigType: 'Geometry/MFL Smart Pig',
      launchTime: '2025-01-31T06:00:00Z',
      currentPosition: 'MP 47.3',
      speed: 4.2,              // mph
      anomaliesDetected: 3,
      wallThinningSuspected: true,
      estimatedArrival: '2025-01-31T18:30:00Z',
    },
  },
  // Environmental
  {
    id: 'ENV-STATION',
    type: 'Weather Station',
    category: 'environmental',
    model: 'Campbell Scientific',
    location: 'Station Met Tower',
    status: 'operational',
    metrics: {
      ambientTemp: 28,         // °F - COLD
      humidity: 85,            // %
      windSpeed: 12,           // mph
      windDirection: 'NNW',
      barometric: 29.92,       // inHg
      dewpoint: 24,            // °F
      hydrateFormationRisk: true,  // Temp near dewpoint at pressure
    },
  },
  // RTU/SCADA
  {
    id: 'RTU-MAIN',
    type: 'Remote Terminal Unit',
    category: 'control',
    model: 'ABB RTU560',
    location: 'Control Building',
    status: 'operational',
    metrics: {
      cpuLoad: 34,
      memoryUsage: 52,
      ioPoints: 847,
      activeAlarms: 4,
      commStatus: 'Primary',
      lastPoll: '2025-01-31T15:00:00Z',
      pollRate: 1,             // seconds
    },
  },
  // Pressure Relief
  {
    id: 'PSV-01',
    type: 'Pressure Relief Valve',
    category: 'safety',
    model: 'Consolidated 1900 Series',
    location: 'Discharge Header',
    status: 'operational',
    metrics: {
      setPoint: 1050,          // psig
      currentPressure: 872,
      lastLift: null,
      lastInspection: '2024-08-15',
      nextInspection: '2025-08-15',
    },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// AI STATUS TYPES - Pipeline specific
// ════════════════════════════════════════════════════════════════════════════

const AI_STATUS = {
  CRITICAL: { icon: '🚨', text: 'CRITICAL', bg: 'bg-red-500', glow: 'from-red-500/50 to-rose-500/50', color: 'text-red-400' },
  WARNING: { icon: '⚠️', text: 'WARNING', bg: 'bg-amber-500', glow: 'from-amber-500/50 to-yellow-500/50', color: 'text-amber-400' },
  ANALYZING: { icon: '🔍', text: 'ANALYZING', bg: 'bg-purple-500', glow: 'from-purple-500/50 to-violet-500/50', color: 'text-purple-400', pulse: true },
  NORMAL: { icon: '✓', text: 'NORMAL', bg: 'bg-emerald-500', glow: 'from-emerald-500/30 to-teal-500/30', color: 'text-emerald-400' },
  STANDBY: { icon: '○', text: 'STANDBY', bg: 'bg-slate-500', glow: 'from-slate-500/30 to-gray-500/30', color: 'text-slate-400' },
};

const CATEGORY_STYLES = {
  compressor: { icon: '🔵', color: 'text-blue-300', bg: 'from-blue-400/30 to-cyan-500/30', border: 'border-blue-400/60', label: 'Compressor' },
  metering: { icon: '📊', color: 'text-cyan-300', bg: 'from-cyan-400/30 to-teal-500/30', border: 'border-cyan-400/60', label: 'Metering' },
  valve: { icon: '🚰', color: 'text-emerald-300', bg: 'from-emerald-400/30 to-green-500/30', border: 'border-emerald-400/60', label: 'Valve' },
  safety: { icon: '⚠️', color: 'text-red-300', bg: 'from-red-400/30 to-orange-500/30', border: 'border-red-400/60', label: 'Safety' },
  inspection: { icon: '🐷', color: 'text-purple-300', bg: 'from-purple-400/30 to-violet-500/30', border: 'border-purple-400/60', label: 'Inspection' },
  environmental: { icon: '🌡️', color: 'text-amber-300', bg: 'from-amber-400/30 to-yellow-500/30', border: 'border-amber-400/60', label: 'Environmental' },
  control: { icon: '🖥️', color: 'text-slate-300', bg: 'from-slate-400/30 to-gray-500/30', border: 'border-slate-400/60', label: 'Control' },
};

// ════════════════════════════════════════════════════════════════════════════
// AI ANOMALY DETECTION - Pipeline Operations
// ════════════════════════════════════════════════════════════════════════════

const detectPipelineAnomalies = (assets, gateway) => {
  const anomalies = [];
  
  assets.forEach(asset => {
    const m = asset.metrics;
    
    // Leak Detection System
    if (asset.category === 'safety' && asset.type === 'Leak Detection System') {
      if (m.status === 'LEAK_SUSPECTED') {
        anomalies.push({
          id: `leak-${asset.id}`,
          type: 'LEAK_DETECTED',
          assetId: asset.id,
          severity: 'critical',
          metric: `${m.confidence}% confidence`,
          description: `Potential leak detected at ${m.estimatedLocation}. Estimated loss: ${m.estimatedRate} MMcf/d`,
          recommendation: 'IMMEDIATE ACTION: Isolate affected section using MOV-47-A and MOV-47-B. Dispatch field crew for visual inspection. Notify PHMSA if confirmed.',
          confidence: m.confidence,
          action: {
            type: 'ISOLATE_SECTION',
            targets: ['MOV-47-A', 'MOV-47-B'],
            description: 'Close isolation valves',
          },
        });
      }
    }
    
    // Compressor Issues
    if (asset.category === 'compressor') {
      // High vibration
      if (m.vibration && m.baselineVibration && m.vibration > m.baselineVibration * 1.3) {
        const percentIncrease = Math.floor((m.vibration / m.baselineVibration - 1) * 100);
        anomalies.push({
          id: `vibration-${asset.id}`,
          type: 'VIBRATION_HIGH',
          assetId: asset.id,
          severity: m.vibration > m.baselineVibration * 1.5 ? 'critical' : 'warning',
          metric: `${m.vibration} in/s (+${percentIncrease}%)`,
          description: `Compressor vibration ${percentIncrease}% above baseline. Possible bearing wear or imbalance.`,
          recommendation: 'Schedule vibration analysis. Plan maintenance window within 72 hours. Monitor bearing temperatures closely.',
          confidence: 89,
          action: {
            type: 'SCHEDULE_MAINTENANCE',
            description: 'Create maintenance work order',
          },
        });
      }
      
      // High bearing temp
      if (m.bearingTemp && m.bearingTemp > 200) {
        anomalies.push({
          id: `bearing-${asset.id}`,
          type: 'BEARING_OVERTEMP',
          assetId: asset.id,
          severity: m.bearingTemp > 220 ? 'critical' : 'warning',
          metric: `${m.bearingTemp}°F`,
          description: 'Bearing temperature elevated. Risk of bearing failure.',
          recommendation: 'Reduce compressor speed. Check oil system. Prepare for unit shutdown if temp continues rising.',
          confidence: 94,
          action: {
            type: 'REDUCE_SPEED',
            description: 'Reduce compressor RPM 10%',
          },
        });
      }
    }
    
    // Valve Faults
    if (asset.category === 'valve') {
      if (asset.status === 'fault') {
        anomalies.push({
          id: `valve-fault-${asset.id}`,
          type: 'VALVE_FAULT',
          assetId: asset.id,
          severity: 'critical',
          metric: m.faultCode || 'FAULT',
          description: `Valve failed to respond. ${m.faultCode || 'Communication error or actuator failure.'}`,
          recommendation: 'Dispatch technician immediately. Valve may require manual operation. Check actuator pressure and power supply.',
          confidence: 97,
          action: {
            type: 'DISPATCH_CREW',
            description: 'Emergency dispatch',
          },
        });
      }
      
      // Slow stroke time
      if (m.strokeTime && m.baselineStroke && m.strokeTime > m.baselineStroke * 1.15) {
        anomalies.push({
          id: `stroke-${asset.id}`,
          type: 'VALVE_DEGRADATION',
          assetId: asset.id,
          severity: 'warning',
          metric: `${m.strokeTime}s (baseline: ${m.baselineStroke}s)`,
          description: 'Valve stroke time increasing. Possible actuator or valve body issue.',
          recommendation: 'Schedule partial stroke test. Plan valve maintenance during next outage.',
          confidence: 82,
        });
      }
    }
    
    // Metering Imbalance
    if (asset.category === 'metering') {
      const inletMeter = assets.find(a => a.id === 'METER-INLET');
      const outletMeter = assets.find(a => a.id === 'METER-OUTLET');
      
      if (inletMeter && outletMeter && asset.id === 'METER-OUTLET') {
        const imbalance = ((inletMeter.metrics.flowRate - outletMeter.metrics.flowRate) / inletMeter.metrics.flowRate) * 100;
        
        if (imbalance > 1.5) {
          anomalies.push({
            id: `imbalance-${asset.id}`,
            type: 'FLOW_IMBALANCE',
            assetId: asset.id,
            severity: imbalance > 2 ? 'critical' : 'warning',
            metric: `${imbalance.toFixed(2)}%`,
            description: `Flow imbalance of ${imbalance.toFixed(2)}% between inlet and outlet meters. Possible leak, meter error, or theft.`,
            recommendation: 'Cross-reference with leak detection. Verify meter calibration. Check for unauthorized taps.',
            confidence: 91,
          });
        }
      }
    }
    
    // Gas Detection
    if (asset.type === 'Gas Detector') {
      if (m.lel >= m.alarmLEL * 0.8) {
        anomalies.push({
          id: `gas-${asset.id}`,
          type: 'GAS_ELEVATED',
          assetId: asset.id,
          severity: m.lel >= m.alarmLEL ? 'critical' : 'warning',
          metric: `${m.lel}% LEL`,
          description: `Combustible gas level elevated at ${asset.location}. Approaching alarm threshold.`,
          recommendation: 'Investigate source of gas. Increase ventilation. Prepare for evacuation if levels rise.',
          confidence: 98,
          action: {
            type: 'INVESTIGATE',
            description: 'Dispatch safety team',
          },
        });
      }
      
      if (m.h2s > 5) {
        anomalies.push({
          id: `h2s-${asset.id}`,
          type: 'H2S_DETECTED',
          assetId: asset.id,
          severity: 'critical',
          metric: `${m.h2s} ppm`,
          description: 'H2S detected! Toxic gas hazard.',
          recommendation: 'EVACUATE IMMEDIATELY. Don breathing apparatus. Activate emergency response plan.',
          confidence: 99,
          action: {
            type: 'EMERGENCY_SHUTDOWN',
            description: 'Initiate ESD',
          },
        });
      }
    }
    
    // Pig Inspection Anomalies
    if (asset.category === 'inspection') {
      if (m.wallThinningSuspected) {
        anomalies.push({
          id: `pig-${asset.id}`,
          type: 'WALL_THINNING',
          assetId: asset.id,
          severity: 'warning',
          metric: `${m.anomaliesDetected} anomalies`,
          description: `Smart pig detected ${m.anomaliesDetected} anomalies including suspected wall thinning near ${m.currentPosition}.`,
          recommendation: 'Schedule detailed data analysis upon pig retrieval. Plan integrity dig if metal loss > 50%.',
          confidence: 78,
          action: {
            type: 'SCHEDULE_DIG',
            description: 'Plan integrity assessment',
          },
        });
      }
    }
    
    // Environmental - Hydrate Risk
    if (asset.category === 'environmental') {
      if (m.hydrateFormationRisk) {
        anomalies.push({
          id: `hydrate-${asset.id}`,
          type: 'HYDRATE_RISK',
          assetId: asset.id,
          severity: 'warning',
          metric: `${m.ambientTemp}°F`,
          description: 'Hydrate formation conditions detected. Risk of pipeline blockage.',
          recommendation: 'Increase glycol/methanol injection rate. Monitor pressure differential across line.',
          confidence: 85,
          action: {
            type: 'INJECT_INHIBITOR',
            description: 'Increase glycol injection',
          },
        });
      }
    }
  });
  
  // Check MAOP approach
  if (gateway.operatingPressure > gateway.maxAllowablePressure * 0.9) {
    anomalies.push({
      id: 'maop-warning',
      type: 'MAOP_APPROACH',
      assetId: 'SYSTEM',
      severity: 'warning',
      metric: `${gateway.operatingPressure} / ${gateway.maxAllowablePressure} psig`,
      description: `Operating pressure at ${Math.floor(gateway.operatingPressure / gateway.maxAllowablePressure * 100)}% of MAOP.`,
      recommendation: 'Reduce throughput or bring standby compressor online. Monitor pressure closely.',
      confidence: 99,
    });
  }
  
  return anomalies.sort((a, b) => {
    const sevOrder = { critical: 0, warning: 1, info: 2 };
    return (sevOrder[a.severity] || 3) - (sevOrder[b.severity] || 3);
  });
};

const getAssetAIStatus = (asset, anomalies) => {
  const assetAnomalies = anomalies.filter(a => a.assetId === asset.id);
  
  if (asset.status === 'standby' || asset.status === 'closed') return AI_STATUS.STANDBY;
  
  const hasCritical = assetAnomalies.some(a => a.severity === 'critical');
  const hasWarning = assetAnomalies.some(a => a.severity === 'warning');
  
  if (hasCritical) return AI_STATUS.CRITICAL;
  if (hasWarning) return AI_STATUS.WARNING;
  if (asset.status === 'alarm' || asset.status === 'fault') return AI_STATUS.CRITICAL;
  return AI_STATUS.NORMAL;
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS
// ════════════════════════════════════════════════════════════════════════════

// Pipeline Operations Center Node
const PipelineOpsNode = ({ data }) => (
  <div className="relative group">
    <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-400 !border-2 !border-white" />
    
    <div className="absolute -inset-2 bg-gradient-to-r from-blue-400/30 via-cyan-400/30 to-blue-400/30 rounded-2xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500" />
    
    <div className="relative bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-2 border-blue-400/50 rounded-2xl p-6 min-w-[280px] shadow-2xl shadow-blue-500/20">
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-400 shadow-lg shadow-emerald-400/50" />
      </div>
      
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-blue-400/30 to-cyan-500/30 rounded-xl flex items-center justify-center border border-blue-400/50">
          <span className="text-3xl">🛢️</span>
        </div>
      </div>
      
      <div className="text-center">
        <div className="text-sm font-semibold text-white tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-300 mt-1">{data.sublabel}</div>
      </div>
      
      <div className="mt-4 pt-3 border-t border-slate-600/50 grid grid-cols-3 gap-2">
        <div className="text-center">
          <div className="text-lg font-bold text-blue-300">{data.throughput || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">MMcf/d</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-300">{data.pressure || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">PSIG</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-amber-300">{data.stations || 0}</div>
          <div className="text-[10px] text-slate-400 uppercase tracking-wider">Stations</div>
        </div>
      </div>
      
      <div className="mt-3 flex items-center justify-center gap-1.5">
        <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
        <span className="text-[10px] text-blue-300 font-medium">SCADA Active</span>
      </div>
    </div>
  </div>
);

// Compressor Station Gateway Node
const StationGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  const pressurePercent = Math.floor((data.operatingPressure / data.maxAllowablePressure) * 100);
  
  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-emerald-400 !border-2 !border-white" />
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      <div className={`absolute -inset-1.5 bg-gradient-to-r ${isOnline ? 'from-emerald-400/30 to-teal-400/30' : 'from-red-400/30 to-orange-400/30'} rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-all duration-300`} />
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${isOnline ? 'border-emerald-400/60' : 'border-red-400/60'} rounded-xl p-4 min-w-[300px] shadow-xl transition-all duration-300 group-hover:scale-105`}>
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-400' : 'bg-red-400'} shadow-lg`} />
        </div>
        
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-500/30' : 'bg-red-500/30'}`}>
            <span className="text-xl">🏭</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white truncate">{data.name}</div>
            <div className="text-[10px] text-slate-400 truncate">{data.location}</div>
          </div>
        </div>
        
        {/* Pressure bar */}
        <div className="mt-3">
          <div className="flex justify-between text-[10px] mb-1">
            <span className="text-slate-400">Operating Pressure</span>
            <span className={`font-bold ${pressurePercent > 90 ? 'text-amber-300' : 'text-emerald-300'}`}>
              {data.operatingPressure} / {data.maxAllowablePressure} psig
            </span>
          </div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${pressurePercent > 90 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${pressurePercent}%` }}
            />
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-t border-slate-500/30 grid grid-cols-3 gap-2 text-xs">
          <div>
            <span className="text-slate-400">Throughput</span>
            <div className="font-semibold text-cyan-300">{data.throughput} MMcf/d</div>
          </div>
          <div className="text-center">
            <span className="text-slate-400">Assets</span>
            <div className="font-semibold text-blue-300">{data.assetCount}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-400">Alarms</span>
            <div className={`font-semibold ${data.activeAlarms > 0 ? 'text-red-300' : 'text-slate-300'}`}>{data.activeAlarms}</div>
          </div>
        </div>
        
        <div className="mt-2 text-center">
          <span className="text-[9px] px-2 py-0.5 bg-slate-800/50 rounded-full text-cyan-300 font-mono">
            {data.stratusModel} • {data.connectivity}
          </span>
        </div>
      </div>
    </div>
  );
};

// Pipeline Asset Node
const PipelineAssetNode = ({ data }) => {
  const categoryStyle = CATEGORY_STYLES[data.category] || CATEGORY_STYLES.control;
  const aiStatus = data.aiStatus || AI_STATUS.NORMAL;
  const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
  const m = data.metrics || {};

  const renderMetrics = () => {
    switch (data.category) {
      case 'compressor':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${m.dischargePressure > 900 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.dischargePressure > 900 ? 'text-amber-300' : 'text-slate-300'}`}>{m.dischargePressure}</div>
              <div className="text-[8px] text-slate-500">PSIG</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.vibration > m.baselineVibration * 1.3 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.vibration > m.baselineVibration * 1.3 ? 'text-red-300' : 'text-slate-300'}`}>{m.vibration}</div>
              <div className="text-[8px] text-slate-500">VIB</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.flowRate || 0}</div>
              <div className="text-[8px] text-slate-500">MMcf</div>
            </div>
          </div>
        );
      case 'metering':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-cyan-300">{m.flowRate}</div>
              <div className="text-[8px] text-slate-500">MMcf/d</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.pressure}</div>
              <div className="text-[8px] text-slate-500">PSIG</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.temperature}°F</div>
              <div className="text-[8px] text-slate-500">TEMP</div>
            </div>
          </div>
        );
      case 'valve':
        return (
          <div className="grid grid-cols-2 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${data.status === 'fault' ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${data.status === 'fault' ? 'text-red-300' : m.position === 100 ? 'text-emerald-300' : 'text-amber-300'}`}>
                {data.status === 'fault' ? 'FAULT' : m.position === 100 ? 'OPEN' : m.position === 0 ? 'CLOSED' : `${m.position}%`}
              </div>
              <div className="text-[8px] text-slate-500">STATUS</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.actuatorPressure}</div>
              <div className="text-[8px] text-slate-500">ACT PSI</div>
            </div>
          </div>
        );
      case 'safety':
        if (data.type === 'Leak Detection System') {
          return (
            <div className={`rounded px-2 py-1.5 ${m.status === 'LEAK_SUSPECTED' ? 'bg-red-500/30 border border-red-400/50' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold text-center ${m.status === 'LEAK_SUSPECTED' ? 'text-red-300' : 'text-emerald-300'}`}>
                {m.status === 'LEAK_SUSPECTED' ? `⚠️ LEAK @ ${m.estimatedLocation}` : '✓ NO LEAK'}
              </div>
            </div>
          );
        }
        if (data.type === 'Gas Detector') {
          return (
            <div className="grid grid-cols-2 gap-1 text-center">
              <div className={`rounded px-1 py-0.5 ${m.lel >= m.alarmLEL * 0.8 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${m.lel >= m.alarmLEL * 0.8 ? 'text-amber-300' : 'text-slate-300'}`}>{m.lel}%</div>
                <div className="text-[8px] text-slate-500">LEL</div>
              </div>
              <div className={`rounded px-1 py-0.5 ${m.h2s > 5 ? 'bg-red-500/20' : 'bg-slate-700/50'}`}>
                <div className={`text-[10px] font-bold ${m.h2s > 5 ? 'text-red-300' : 'text-slate-300'}`}>{m.h2s}</div>
                <div className="text-[8px] text-slate-500">H₂S</div>
              </div>
            </div>
          );
        }
        return null;
      case 'inspection':
        return (
          <div className="rounded px-2 py-1.5 bg-purple-500/20 border border-purple-400/30">
            <div className="text-[10px] font-bold text-purple-300 text-center">
              🐷 {m.currentPosition} • {m.speed} mph
            </div>
            {m.anomaliesDetected > 0 && (
              <div className="text-[9px] text-amber-300 text-center mt-1">
                {m.anomaliesDetected} anomalies detected
              </div>
            )}
          </div>
        );
      case 'environmental':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className={`rounded px-1 py-0.5 ${m.ambientTemp < 32 ? 'bg-cyan-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.ambientTemp < 32 ? 'text-cyan-300' : 'text-slate-300'}`}>{m.ambientTemp}°F</div>
              <div className="text-[8px] text-slate-500">TEMP</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.windSpeed}</div>
              <div className="text-[8px] text-slate-500">WIND</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.hydrateFormationRisk ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.hydrateFormationRisk ? 'text-amber-300' : 'text-emerald-300'}`}>
                {m.hydrateFormationRisk ? 'RISK' : 'OK'}
              </div>
              <div className="text-[8px] text-slate-500">HYDRATE</div>
            </div>
          </div>
        );
      case 'control':
        return (
          <div className="grid grid-cols-3 gap-1 text-center">
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-slate-300">{m.cpuLoad}%</div>
              <div className="text-[8px] text-slate-500">CPU</div>
            </div>
            <div className="rounded px-1 py-0.5 bg-slate-700/50">
              <div className="text-[10px] font-bold text-cyan-300">{m.ioPoints}</div>
              <div className="text-[8px] text-slate-500">I/O</div>
            </div>
            <div className={`rounded px-1 py-0.5 ${m.activeAlarms > 0 ? 'bg-amber-500/20' : 'bg-slate-700/50'}`}>
              <div className={`text-[10px] font-bold ${m.activeAlarms > 0 ? 'text-amber-300' : 'text-slate-300'}`}>{m.activeAlarms}</div>
              <div className="text-[8px] text-slate-500">ALARMS</div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative group cursor-pointer">
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-blue-400 !border-2 !border-white" />
      
      {hasAlert && (
        <div className={`absolute -inset-2 bg-gradient-to-r ${aiStatus.glow} rounded-xl blur-lg opacity-60 ${aiStatus.pulse ? 'animate-pulse' : ''}`} />
      )}
      
      <div className={`relative bg-gradient-to-br from-slate-600 to-slate-700 border-2 ${hasAlert ? (aiStatus === AI_STATUS.CRITICAL ? 'border-red-400' : 'border-amber-400') : categoryStyle.border} rounded-lg p-3 min-w-[170px] shadow-lg transition-all duration-300 group-hover:scale-105`}>
        
        {/* AI Status Badge */}
        <div className={`absolute -top-2 -right-2 px-2 py-0.5 ${aiStatus.bg} text-white text-[9px] font-bold rounded-full shadow-lg flex items-center gap-1 ${aiStatus.pulse ? 'animate-pulse' : ''}`}>
          <span>{aiStatus.icon}</span>
          <span>{aiStatus.text}</span>
        </div>
        
        {/* Asset header */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br ${categoryStyle.bg}`}>
            <span className="text-xl">{categoryStyle.icon}</span>
          </div>
          <div>
            <div className="text-xs font-bold text-white">{data.id}</div>
            <div className="text-[10px] text-slate-400">{categoryStyle.label}</div>
          </div>
        </div>
        
        {/* Location */}
        <div className="text-[10px] text-slate-500 mb-2">{data.location}</div>
        
        {/* Metrics */}
        {renderMetrics()}
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  pipelineOps: PipelineOpsNode,
  stationGateway: StationGatewayNode,
  pipelineAsset: PipelineAssetNode,
};

// ════════════════════════════════════════════════════════════════════════════
// AI ACTIVITY PANEL
// ════════════════════════════════════════════════════════════════════════════

const AIActivityPanel = ({ isOpen, onClose, anomalies, assets, gateway }) => {
  const [activeTab, setActiveTab] = useState('alarms');
  const [executingAction, setExecutingAction] = useState(null);
  
  if (!isOpen) return null;
  
  const handleExecuteAction = async (anomaly) => {
    setExecutingAction(anomaly.id);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setExecutingAction(null);
  };
  
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  
  return (
    <div className="absolute top-0 right-0 h-full w-[420px] bg-slate-900/98 backdrop-blur-xl border-l border-slate-700/50 shadow-2xl z-50 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 px-4 py-3 border-b border-slate-700/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <span className="text-lg">🛢️</span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">AI Sentry Pipeline Monitor</h3>
              <div className="text-[10px] text-slate-400">{anomalies.length} alerts • {gateway.pipelineSegment}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 mt-3">
          <div className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 rounded text-emerald-300 text-xs">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            SCADA Online
          </div>
          <div className="text-[10px] text-slate-500">PHMSA Compliant</div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700/50">
        <button
          onClick={() => setActiveTab('alarms')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'alarms'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          <span className="flex items-center justify-center gap-1">
            🚨 Alarms
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{criticalCount}</span>
            )}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('response')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'response'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          ⚡ Response
        </button>
        <button
          onClick={() => setActiveTab('integrity')}
          className={`flex-1 px-4 py-2 text-xs font-medium transition-colors ${
            activeTab === 'integrity'
              ? 'text-cyan-300 border-b-2 border-cyan-400 bg-cyan-500/10'
              : 'text-slate-400 hover:text-white'
          }`}
        >
          🔧 Integrity
        </button>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'alarms' && (
          <>
            {anomalies.length === 0 ? (
              <div className="text-center text-slate-500 py-8">
                <div className="text-3xl mb-2">✓</div>
                <div>All systems normal</div>
              </div>
            ) : (
              anomalies.map(anomaly => {
                const asset = assets.find(a => a.id === anomaly.assetId);
                const categoryStyle = CATEGORY_STYLES[asset?.category] || CATEGORY_STYLES.control;
                return (
                  <div
                    key={anomaly.id}
                    className={`bg-slate-800/50 rounded-lg border ${
                      anomaly.severity === 'critical' ? 'border-red-500/50' : 'border-amber-500/30'
                    } p-3`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{categoryStyle.icon}</span>
                        <div>
                          <div className="text-sm font-semibold text-white">{anomaly.assetId}</div>
                          <div className="text-[10px] text-slate-400">{asset?.location}</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-xs font-bold ${anomaly.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`}>
                          {anomaly.confidence}%
                        </div>
                        <div className="text-[10px] text-slate-500">confidence</div>
                      </div>
                    </div>
                    
                    <div className="text-xs text-slate-300 mb-2">{anomaly.description}</div>
                    
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {anomaly.metric}
                      </span>
                      <span className="text-[10px] text-slate-500">{anomaly.type.replace(/_/g, ' ')}</span>
                    </div>
                    
                    <div className="bg-slate-700/30 rounded p-2 mb-3">
                      <div className="text-[10px] text-slate-500 uppercase mb-1">AI Recommendation</div>
                      <div className="text-xs text-cyan-300">{anomaly.recommendation}</div>
                    </div>
                    
                    {anomaly.action && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleExecuteAction(anomaly)}
                          disabled={executingAction === anomaly.id}
                          className={`flex-1 px-3 py-1.5 ${anomaly.severity === 'critical' ? 'bg-red-500 hover:bg-red-400' : 'bg-cyan-500 hover:bg-cyan-400'} disabled:bg-slate-600 text-white text-xs font-medium rounded transition-colors flex items-center justify-center gap-1`}
                        >
                          {executingAction === anomaly.id ? (
                            <>
                              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Executing...
                            </>
                          ) : (
                            <>✓ {anomaly.action.type.replace(/_/g, ' ')}</>
                          )}
                        </button>
                        <button className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded transition-colors">
                          Acknowledge
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
        
        {activeTab === 'response' && (
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-red-500/20 to-amber-500/20 rounded-lg border border-red-500/30 p-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🚨</span>
                <div>
                  <div className="text-sm font-semibold text-white">Emergency Response</div>
                  <div className="text-[10px] text-slate-400">Section Isolation Protocol</div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-lg font-bold text-red-400">87%</div>
                  <div className="text-[10px] text-slate-500">Confidence</div>
                </div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Detected Event</div>
                <div className="text-sm text-red-300">Potential Leak - Section 7</div>
                <div className="text-xs text-slate-400 mt-1">MP 47.3 ± 0.2 • Est. 2.4 MMcf/d loss</div>
              </div>
              
              <div className="bg-slate-800/50 rounded p-3 mb-3">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Recommended Actions</div>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-red-500/30 flex items-center justify-center text-red-300">1</span>
                    <span className="text-slate-300">Close MOV-47-A (upstream isolation)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-red-500/30 flex items-center justify-center text-red-300">2</span>
                    <span className="text-slate-300">Close MOV-47-B (downstream isolation)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-amber-500/30 flex items-center justify-center text-amber-300">3</span>
                    <span className="text-slate-300">Dispatch field crew for verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded bg-amber-500/30 flex items-center justify-center text-amber-300">4</span>
                    <span className="text-slate-300">Notify PHMSA if confirmed</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded p-2 mb-3">
                <div className="text-[10px] text-amber-300 font-semibold">⚠️ MOV-47-B FAULT - Manual intervention required</div>
              </div>
              
              <div className="flex gap-2">
                <button className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-400 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
                  🛑 Isolate Section
                </button>
                <button className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 text-sm rounded-lg transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
            
            <div className="text-center text-[10px] text-slate-500 pt-2">
              Powered by <span className="text-cyan-400">AI Sentry</span> • CFR 49 Part 192 Compliant
            </div>
          </div>
        )}
        
        {activeTab === 'integrity' && (
          <div className="space-y-3">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Pipeline Integrity Status</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-cyan-300">98.4%</div>
                  <div className="text-xs text-slate-400">Line Integrity</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-emerald-300">847</div>
                  <div className="text-xs text-slate-400">Days Since Incident</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-amber-300">3</div>
                  <div className="text-xs text-slate-400">Anomalies Detected</div>
                </div>
                <div className="bg-slate-700/50 rounded p-3">
                  <div className="text-2xl font-bold text-purple-300">2</div>
                  <div className="text-xs text-slate-400">Digs Scheduled</div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Smart Pig Results</div>
              <div className="bg-purple-500/10 border border-purple-500/30 rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🐷</span>
                  <div>
                    <div className="text-sm font-semibold text-white">Geometry/MFL Run</div>
                    <div className="text-[10px] text-slate-400">In Progress - MP 47.3</div>
                  </div>
                </div>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Anomaly #1</span>
                    <span className="text-amber-300">Wall thinning - MP 42.8</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Anomaly #2</span>
                    <span className="text-amber-300">Dent - MP 45.1</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Anomaly #3</span>
                    <span className="text-red-300">Corrosion suspected - MP 47.3</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-xs text-slate-400 uppercase mb-3">Upcoming Maintenance</div>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-300">COMP-01 Overhaul</span>
                  <span className="text-cyan-300">3,750 hrs</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-300">MOV-47-B Repair</span>
                  <span className="text-red-300">IMMEDIATE</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-300">METER-OUTLET Cal</span>
                  <span className="text-amber-300">Feb 1</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="border-t border-slate-700/50 px-4 py-2 bg-slate-800/50">
        <div className="flex items-center justify-between text-[10px] text-slate-500">
          <span>Last SCADA poll: 1s ago</span>
          <button className="text-cyan-400 hover:text-cyan-300">Refresh Now</button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// PROACTIVE ALERT CARD
// ════════════════════════════════════════════════════════════════════════════

const ProactiveAlert = ({ anomalies, assets, onViewDetails }) => {
  const criticalAnomalies = anomalies.filter(a => a.severity === 'critical');
  if (criticalAnomalies.length === 0) return null;
  
  // Prioritize leak detection
  const leakAnomaly = criticalAnomalies.find(a => a.type === 'LEAK_DETECTED');
  const topAnomaly = leakAnomaly || criticalAnomalies[0];
  const asset = assets.find(a => a.id === topAnomaly.assetId);
  
  return (
    <div className="absolute top-32 left-6 z-40 animate-slide-in-left">
      <div className="bg-gradient-to-r from-red-900/95 to-slate-900/95 backdrop-blur-xl border border-red-500/50 rounded-xl p-4 shadow-2xl shadow-red-500/20 max-w-md">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 bg-red-500/30 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-2xl animate-pulse">🚨</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white mb-1">
              {topAnomaly.type === 'LEAK_DETECTED' ? '⚠️ POTENTIAL LEAK DETECTED' : 
               topAnomaly.type === 'VALVE_FAULT' ? 'VALVE FAULT' :
               'PIPELINE ALERT'}
            </div>
            <div className="text-xs text-red-200 mb-2">
              {topAnomaly.description}
            </div>
            <div className="text-[10px] text-slate-400 mb-3">
              {topAnomaly.assetId} • {asset?.location} • {criticalAnomalies.length > 1 ? `+${criticalAnomalies.length - 1} more` : ''}
            </div>
            <div className="flex gap-2">
              <button 
                onClick={onViewDetails}
                className="px-4 py-1.5 bg-red-500 hover:bg-red-400 text-white text-xs font-medium rounded-lg transition-colors"
              >
                View Details
              </button>
              {topAnomaly.type === 'LEAK_DETECTED' && (
                <button className="px-4 py-1.5 bg-amber-500 hover:bg-amber-400 text-white text-xs font-medium rounded-lg transition-colors">
                  🛑 Isolate Now
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ gateway, anomalies, onOpenPanel }) => {
  const criticalCount = anomalies.filter(a => a.severity === 'critical').length;
  const pressurePercent = Math.floor((gateway.operatingPressure / gateway.maxAllowablePressure) * 100);
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-slate-600/30">
        <span className="text-cyan-300 font-bold text-lg">{gateway.throughput}</span>
        <span className="text-slate-300 text-xs">MMcf/d</span>
      </div>
      <div className={`flex items-center gap-2 rounded-lg px-3 py-2 border ${pressurePercent > 90 ? 'bg-amber-500/20 border-amber-400/50' : 'bg-slate-700/50 border-slate-600/30'}`}>
        <span className={`font-bold text-lg ${pressurePercent > 90 ? 'text-amber-300' : 'text-emerald-300'}`}>{gateway.operatingPressure}</span>
        <span className="text-slate-300 text-xs">PSIG</span>
      </div>
      {criticalCount > 0 && (
        <div className="flex items-center gap-2 bg-red-500/20 rounded-lg px-3 py-2 border border-red-400/50 animate-pulse">
          <span className="text-red-300 font-bold text-lg">{criticalCount}</span>
          <span className="text-red-300/70 text-xs">CRITICAL</span>
        </div>
      )}
      <button
        onClick={onOpenPanel}
        className="flex items-center gap-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg px-4 py-2 border border-blue-400/50 transition-colors"
      >
        <span className="text-blue-300 text-sm">🤖</span>
        <span className="text-blue-300 text-xs font-medium">AI Sentry</span>
        {anomalies.length > 0 && (
          <span className="px-1.5 py-0.5 bg-blue-500 text-white text-[10px] rounded-full">{anomalies.length}</span>
        )}
      </button>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-600/50 rounded-xl p-4 space-y-3">
    <div className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      {Object.entries(CATEGORY_STYLES).map(([key, style]) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-sm">{style.icon}</span>
          <span className="text-xs text-slate-300">{style.label}</span>
        </div>
      ))}
    </div>
    <div className="pt-2 border-t border-slate-600/50 space-y-2">
      <div className="text-[10px] text-slate-400 uppercase">Status</div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-red-500" />
        <span className="text-xs text-slate-300">Critical</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-amber-500" />
        <span className="text-xs text-slate-300">Warning</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-emerald-500" />
        <span className="text-xs text-slate-300">Normal</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════

const PipelineSentry = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [assets] = useState(MOCK_ASSETS);
  const [gateway] = useState(MOCK_STATION_GATEWAY);
  const [anomalies, setAnomalies] = useState([]);
  const [isAIPanelOpen, setIsAIPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Build topology
  const buildTopology = useCallback(() => {
    const detectedAnomalies = detectPipelineAnomalies(assets, gateway);
    setAnomalies(detectedAnomalies);
    
    const newNodes = [];
    const newEdges = [];
    
    // Layout constants
    const CLOUD_Y = 0;
    const GATEWAY_Y = 200;
    const ASSET_Y_START = 440;
    const CENTER_X = 600;
    const ASSET_GAP = 190;
    const CATEGORY_GAP = 180;
    
    // Group assets by category
    const categories = ['compressor', 'metering', 'valve', 'safety', 'inspection', 'environmental', 'control'];
    const assetsByCategory = {};
    categories.forEach(cat => {
      assetsByCategory[cat] = assets.filter(a => a.category === cat);
    });
    
    // 1. Pipeline Operations Center (Cloud)
    newNodes.push({
      id: 'pipeline-ops',
      type: 'pipelineOps',
      position: { x: CENTER_X, y: CLOUD_Y },
      data: {
        label: 'Pipeline Operations',
        sublabel: 'Gulf Coast Control Center',
        throughput: gateway.throughput,
        pressure: gateway.operatingPressure,
        stations: 1,
      },
    });
    
    // 2. Station Gateway
    newNodes.push({
      id: `gateway-${gateway.id}`,
      type: 'stationGateway',
      position: { x: CENTER_X, y: GATEWAY_Y },
      data: { ...gateway, activeAlarms: detectedAnomalies.length },
    });
    
    // Edge: Cloud → Gateway
    newEdges.push({
      id: 'cloud-to-gateway',
      source: 'pipeline-ops',
      target: `gateway-${gateway.id}`,
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#3b82f6', strokeWidth: 2 },
    });
    
    // 3. Assets by category
    let currentY = ASSET_Y_START;
    
    categories.forEach(category => {
      const categoryAssets = assetsByCategory[category];
      if (categoryAssets.length === 0) return;
      
      const startX = CENTER_X - ((categoryAssets.length - 1) * ASSET_GAP) / 2;
      
      categoryAssets.forEach((asset, idx) => {
        const aiStatus = getAssetAIStatus(asset, detectedAnomalies);
        const assetId = `asset-${asset.id}`;
        
        newNodes.push({
          id: assetId,
          type: 'pipelineAsset',
          position: { x: startX + idx * ASSET_GAP, y: currentY },
          data: { ...asset, aiStatus },
        });
        
        // Edge: Gateway → Asset
        const hasAlert = aiStatus === AI_STATUS.CRITICAL || aiStatus === AI_STATUS.WARNING;
        const edgeColor = hasAlert 
          ? (aiStatus === AI_STATUS.CRITICAL ? '#ef4444' : '#f59e0b')
          : '#3b82f6';
        
        newEdges.push({
          id: `gateway-to-${assetId}`,
          source: `gateway-${gateway.id}`,
          target: assetId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: edgeColor, strokeWidth: 2 },
        });
      });
      
      currentY += CATEGORY_GAP;
    });
    
    return { nodes: newNodes, edges: newEdges };
  }, [assets, gateway]);

  // Initialize
  useEffect(() => {
    setLoading(true);
    
    setTimeout(() => {
      const { nodes: newNodes, edges: newEdges } = buildTopology();
      setNodes(newNodes);
      setEdges(newEdges);
      setLoading(false);
    }, 1000);
  }, [buildTopology, setNodes, setEdges]);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-slate-900/95 to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-blue-500/30 to-cyan-600/30 rounded-xl flex items-center justify-center border border-blue-400/30">
                <span className="text-xl">🛢️</span>
              </span>
              Pipeline Sentry
            </h1>
            <p className="text-slate-300 text-sm mt-1">AI-Powered Pipeline Operations • Leak Detection • Integrity Management</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] px-2 py-1 bg-blue-500/20 text-blue-300 rounded border border-blue-500/30">
                {gateway.pipelineSegment}
              </span>
              <span className="text-[10px] px-2 py-1 bg-slate-700/50 text-slate-400 rounded">
                MWC Barcelona 2025
              </span>
            </div>
          </div>
          <StatsBar 
            gateway={gateway}
            anomalies={anomalies}
            onOpenPanel={() => setIsAIPanelOpen(true)} 
          />
        </div>
      </div>
      
      {/* Proactive Alert */}
      <ProactiveAlert 
        anomalies={anomalies} 
        assets={assets} 
        onViewDetails={() => setIsAIPanelOpen(true)} 
      />
      
      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-slate-900/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-blue-300 text-lg font-medium">AI Sentry Initializing...</div>
            <div className="text-slate-400 text-sm mt-1">Connecting to SCADA System</div>
          </div>
        </div>
      )}
      
      {/* React Flow */}
      {!loading && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.15 }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.45 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant="dots" gap={20} size={1} color="rgba(59, 130, 246, 0.15)" />
          <Controls className="!bg-slate-700/90 !border-slate-600/50 !rounded-xl" />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'pipelineOps') return '#3b82f6';
              if (node.type === 'stationGateway') return '#34d399';
              if (node.data?.aiStatus === AI_STATUS.CRITICAL) return '#ef4444';
              if (node.data?.aiStatus === AI_STATUS.WARNING) return '#f59e0b';
              return '#60a5fa';
            }}
            maskColor="rgba(15, 23, 42, 0.8)"
            className="!bg-slate-800/90 !border-slate-600/50 rounded-xl"
          />
          <Panel position="bottom-left"><Legend /></Panel>
        </ReactFlow>
      )}
      
      {/* AI Panel */}
      <AIActivityPanel 
        isOpen={isAIPanelOpen} 
        onClose={() => setIsAIPanelOpen(false)} 
        anomalies={anomalies}
        assets={assets}
        gateway={gateway}
      />
      
      {/* Animations */}
      <style>{`
        @keyframes slide-in-left { from { opacity: 0; transform: translateX(-20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slide-in-right { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in-left { animation: slide-in-left 0.3s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default PipelineSentry;
